import React, { createContext, useState, useContext, useEffect } from "react";
import config from "../config";

// Create the context
const ChatbotContext = createContext(null);

// Custom hook to use the chatbot context
export const useChatbots = () => useContext(ChatbotContext);

// Provider component
export const ChatbotProvider = ({ children }) => {
  const [chatbotState, setChatbotState] = useState({
    chatbots: [],
    loading: true,
    error: null,
  });

  // Fetch chatbots only once when the provider mounts
  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.dashboard.chatbots}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json();

        if (data.success) {
          setChatbotState({
            chatbots: data.data,
            loading: false,
            error: null,
          });
        } else {
          setChatbotState({
            chatbots: [],
            loading: false,
            error: "Failed to load chatbots",
          });
        }
      } catch (error) {
        console.error("Error fetching chatbots:", error);
        setChatbotState({
          chatbots: [],
          loading: false,
          error: "Failed to load chatbots",
        });
      }
    };

    fetchChatbots();
  }, []);

  // Function to refresh chatbots
  const refreshChatbots = async () => {
    setChatbotState((prev) => ({ ...prev, loading: true }));

    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.dashboard.chatbots}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        setChatbotState({
          chatbots: data.data,
          loading: false,
          error: null,
        });
      } else {
        setChatbotState((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to refresh chatbots",
        }));
      }
    } catch (error) {
      console.error("Error refreshing chatbots:", error);
      setChatbotState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to refresh chatbots",
      }));
    }
  };

  // Helper function to get a chatbot by ID
  const getChatbotById = (id) => {
    return chatbotState.chatbots.find((chatbot) => chatbot._id === id);
  };

  // Helper function to get child chatbots of a parent
  const getChildChatbots = (parentId) => {
    const children = chatbotState.chatbots.filter(
      (chatbot) => chatbot.parent === parentId
    );

    // Sort children alphabetically by title
    return children.sort((a, b) => a.title.localeCompare(b.title));
  };

  // Helper function to get root chatbots (no parent)
  const getRootChatbots = () => {
    const rootChatbots = chatbotState.chatbots.filter((chatbot) => chatbot.parent === "");
    
    // Sort root chatbots by date created (oldest to newest)
    // Date format from backend is MM-DD-YYYY (e.g., "04-01-2025")
    // Safari requires explicit date parsing for MM-DD-YYYY format
    return rootChatbots.sort((a, b) => {
      // Parse MM-DD-YYYY format manually for Safari compatibility
      const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const [month, day, year] = dateStr.split('-');
        // Create date in YYYY-MM-DD format which Safari understands
        return new Date(`${year}-${month}-${day}`);
      };
      
      const dateA = parseDate(a.create_date);
      const dateB = parseDate(b.create_date);
      return dateA - dateB;
    });
  };

  // Helper function to get all chatbots
  const getAllChatbots = () => {
    return chatbotState.chatbots;
  };

  // Enhanced value to provide, including helper methods
  const contextValue = {
    ...chatbotState,
    getChatbotById,
    getChildChatbots,
    getRootChatbots,
    getAllChatbots,
    refreshChatbots,
  };

  return (
    <ChatbotContext.Provider value={contextValue}>
      {children}
    </ChatbotContext.Provider>
  );
};

export default ChatbotContext;
